/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';

import { ROOT_URL_V2, getCSRFToken } from '../../../../config';
import history from '../../../../history';
import PopupHeader from '../../../common/PopupHeader';
import LoadingSpinner from '../../../common/LoadingSpinner';
import actions from '../../../../actions';
import { AgentShiftForm } from '../AgentShiftForm';
import { getAllSkills, getErrorMessages } from '../../../../utils';
import { TJobItem } from '../../../../types';
import { fetchSeries } from '../../../../actions/Series';
import { TRootState } from '../../../../reducers';

export type TDuplicateShiftContainer = {
    match: {
        params: {
            jobId: number;
        };
    };
    notification: {
        info?: (title: string, message: string, time?: string) => void;
        success?: (title: string, message: string, time?: string) => void;
        error?: (title: string, message: string, time?: string) => void;
    };
};

export function DuplicateShiftContainer({
    match,
    notification,
}: TDuplicateShiftContainer) {
    const dispatch = useDispatch();

    const [job, setJob] = useState(null);
    const [roleExtras, setRoleExtras] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const {
        minStart,
        servicesStructure,
    } = useSelector((state: TRootState) => ({
        minStart: state.requestData.minStart,
        servicesStructure: state.servicesStructure,
    }));

    const { jobId } = match.params;
    const currentUrlParams = new URLSearchParams(history.location.search);

    useEffect(() => {
        async function getData() {
            try {
                const response = await axios({
                    method: 'GET',
                    url: `${ROOT_URL_V2}jobs/${jobId}`,
                });

                const {
                    duration,
                    service,
                    additional_services,
                    number_of_freelancers,
                    freelancer_pay_per_hour,
                    client_pay_per_hour,
                    address1,
                    address2,
                    postcode,
                    city,
                    payment_method,
                    matching_mode,
                    breaks,
                    backups_enabled,
                    client_full,
                    freelancers_information,
                    admin_information,
                    nature_of_business,
                    required_qualifications,
                    safety_risks,
                    available_facilities,
                    expenses_payable,
                    title,
                    default_nature_of_business,
                    default_required_qualifications,
                    default_safety_risks,
                    default_available_facilities,
                    default_expenses_payable,
                    rates,
                    consultant_splits,
                    dbs_check,
                    bookings,
                    qualifying_questions,
                    start_datetime,
                } = response.data.content;

                let { date, start_time } = response.data.content;

                // Handle duplicating previous shifts (that started before minimum job start date/time)
                const startDateTime = moment(start_datetime);
                const minStartDateTime = moment(minStart);
                if (startDateTime.isBefore(minStartDateTime)) {
                    date = minStartDateTime.format('YYYY-MM-DD');
                    start_time = minStartDateTime.format('HH:mm');
                }

                const jobData = {
                    date,
                    duration,
                    service,
                    additional_services,
                    number_of_freelancers,
                    start_time: moment(start_time, 'HH:mm:ss').format('HH:mm'),
                    freelancer_pay_per_hour,
                    client_pay_per_hour,
                    address1,
                    address2,
                    postcode,
                    city,
                    payment_method,
                    matching_mode,
                    breaks,
                    backups_enabled,
                    client: client_full.id,
                    freelancers_information,
                    admin_information,
                    nature_of_business,
                    required_qualifications,
                    safety_risks,
                    available_facilities,
                    expenses_payable,
                    title,
                    default_nature_of_business,
                    default_required_qualifications,
                    default_safety_risks,
                    default_available_facilities,
                    default_expenses_payable,
                    client_full,
                    rates,
                    consultant_splits,
                    dbs_check,
                    bookings,
                    qualifying_questions,
                };
                const roleExtrasData = getAllSkills(servicesStructure)
                    .reduce((result: {[key: string]: any}, skill) => ({
                        ...result,
                        [skill]: response.data.content[skill],
                    }), {});
                roleExtrasData.role = response.data.content.role;
                roleExtrasData.vehicle_type = response.data.content.vehicle_type;

                if (roleExtrasData.vehicle_type === 4) {
                    setErrorMessage(
                        'You cannot create new job for \'Motorcycle\'. Please select another vehicle type.'
                    );
                }

                setJob(jobData);
                setRoleExtras(roleExtrasData);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);
            }
        }

        getData();
    }, [jobId]);

    const handleFormSubmit = (formValues: never) => {
        setIsLoading(true);

        axios({
            method: 'post',
            url: `${ROOT_URL_V2}jobs/`,
            data: formValues,
            headers: {
                'X-CSRFToken': getCSRFToken(),
            },
        })
            .then(response => {
                const queryParams = new URLSearchParams();

                const jobSeries = Array.isArray(response?.data?.content)
                    ? response?.data?.content.find(
                        (item: TJobItem) => item.series_reference_number,
                      )
                    : response?.data?.content?.series_reference_number;

                let referenceNumber;

                if (jobSeries) {
                    referenceNumber = response.data.content.map((item: TJobItem) =>
                        item.reference_number).join(', ');
                    queryParams.set('search', jobSeries.series_reference_number);
                    queryParams.set('getall', '1');
                    history.push({
                        pathname: '/series/list',
                        search: queryParams.toString(),
                    });
                    dispatch(fetchSeries(queryParams));
                } else {
                    referenceNumber = response.data.content.reference_number;
                    queryParams.set('search', referenceNumber);
                    queryParams.set('getall', '1');
                    history.push({
                        pathname: '/jobs/list',
                        search: queryParams.toString(),
                    });
                    dispatch(actions.loadShifts(queryParams));
                }

                notification.success(
                    'Success',
                    `${referenceNumber} successfully created`,
                );

            })
            .catch(error => {
                setIsLoading(false);
                setErrorMessage(getErrorMessages(error));
            });
    };

    const onDismiss = () => {
        history.push({
            pathname: '/jobs/list',
            search: currentUrlParams.toString(),
        });
    };

    return (
        <div className="create-shift">
            <PopupHeader title="&nbsp;" onDismiss={onDismiss} />
            {job && roleExtras ? (
                <AgentShiftForm
                    defaultJob={job}
                    handleFormSubmit={handleFormSubmit}
                    mode="duplicate"
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    roleExtras={roleExtras}
                />
            ) : (
                <LoadingSpinner
                    className="shift-spinner"
                    backgroundColor="white"
                />
            )}
        </div>
    );
}
